import React, { useMemo, useState } from 'react';

import { colors } from '@/assets/styles/colors';
import { EmployeeDimensionalFilter } from '@/components/features/attendance/EmployeeDimensionalFilter';
import { PermissionGuard } from '@/components/layout';
import PageLayout from '@/components/layout/PageLayout';
import { Banner } from '@/components/ui/banners';
import { LinkButton } from '@/components/ui/buttons';
import { IndicatorCard } from '@/components/ui/cards';
import { DynamicFiltersCard } from '@/components/ui/cards/DynamicFiltersCard';
import type { FilterField } from '@/components/ui/cards/DynamicFiltersCard';
import { IconScout } from '@/components/ui/images/IconScout';
import { Selector } from '@/components/ui/inputs/selector';
import { Switch } from '@/components/ui/inputs';
import Table from '@/components/ui/tables/Table';
import { useAbsenceApproval } from '@/hooks/absence/useAbsenceApproval';
import { useTranslation } from '@/i18n';


export const AbsenceApproval: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    isLoading,
    onSearch,
    onSortChange,
    companyOptions,
    selectedCompanyValue,
    onCompanyChange,
    yearOptions,
    monthOptions,
    descriptionOptions,
    selectedYear,
    selectedMonth,
    selectedMonthlyCutId,
    onYearChange,
    onMonthChange,
    onDescriptionChange,
    isLoadingCuts,
    arePeriodFiltersDisabled,
    noCutsMessageParts,
    counters,
    onCounterSelect,
    isDecimalFormat,
    onHourFormatToggle,
    dimensionalConfig,
    dimensionalFilter,
    isAdvancedFiltersDisabled,
    isCompanyLocked,
    canDownload,
    isMonthlyCutClosed,
    isRecalculating,
  } = useAbsenceApproval();

  const filterFields: FilterField[] = useMemo(() => [
    {
      id: 'company',
      type: 'custom',
      colSpan: 1,
      loading: companyOptions.length === 0,
      component: (
        <Selector
          label={translate('absence:approval.filters.company')}
          value={selectedCompanyValue}
          onChange={(value) => onCompanyChange(Array.isArray(value) ? (value[0] ?? '') : value)}
          options={companyOptions}
          placeholder={translate('absence:approval.filters.selectCompany')}
          data-testid="absence-approval-company-selector"
        />
      ),
    },
    {
      id: 'year',
      type: 'custom',
      colSpan: 1,
      loading: isLoadingCuts,
      component: (
        <Selector
          label={translate('absence:approval.filters.year')}
          value={selectedYear !== null ? String(selectedYear) : ''}
          onChange={(value) => onYearChange(Array.isArray(value) ? (value[0] ?? '') : value)}
          options={yearOptions}
          disabled={arePeriodFiltersDisabled}
          placeholder={translate('absence:approval.filters.selectYear')}
          data-testid="absence-approval-year-selector"
        />
      ),
    },
    {
      id: 'month',
      type: 'custom',
      colSpan: 1,
      loading: isLoadingCuts,
      component: (
        <Selector
          label={translate('absence:approval.filters.month')}
          value={selectedMonth !== null ? String(selectedMonth) : ''}
          onChange={(value) => onMonthChange(Array.isArray(value) ? (value[0] ?? '') : value)}
          options={monthOptions}
          disabled={arePeriodFiltersDisabled}
          placeholder={translate('absence:approval.filters.selectMonth')}
          data-testid="absence-approval-month-selector"
        />
      ),
    },
    {
      id: 'description',
      type: 'custom',
      colSpan: 1,
      loading: isLoadingCuts,
      component: (
        <Selector
          label={translate('absence:approval.filters.description')}
          value={selectedMonthlyCutId !== null ? String(selectedMonthlyCutId) : ''}
          onChange={(value) => onDescriptionChange(Array.isArray(value) ? (value[0] ?? '') : value)}
          options={descriptionOptions}
          disabled={arePeriodFiltersDisabled}
          placeholder={translate('absence:approval.filters.selectDescription')}
          data-testid="absence-approval-description-selector"
        />
      ),
    },
  ], [
    translate,
    companyOptions,
    selectedCompanyValue,
    onCompanyChange,
    yearOptions,
    monthOptions,
    descriptionOptions,
    selectedYear,
    selectedMonth,
    selectedMonthlyCutId,
    onYearChange,
    onMonthChange,
    onDescriptionChange,
    isLoadingCuts,
    arePeriodFiltersDisabled,
  ]);

  // Descartar el aviso de "sin cortes" se ancla a la empresa: cambiar de empresa (incluida
  // "Todos") vuelve a mostrarlo si la nueva selección también carece de cortes.
  const [dismissedNoCutsScope, setDismissedNoCutsScope] = useState<string | null>(null);
  const showNoCutsMessage = noCutsMessageParts !== null
    && selectedCompanyValue !== dismissedNoCutsScope;

  // El descarte de recálculo/cierre se ancla al corte: cambiar a otro corte que también esté
  // bloqueado vuelve a mostrar el aviso correspondiente.
  const [dismissedRecalculationCutId, setDismissedRecalculationCutId] =
    useState<number | null>(null);
  const showRecalculatingMessage = isRecalculating
    && selectedMonthlyCutId !== dismissedRecalculationCutId;

  const [dismissedClosedCutId, setDismissedClosedCutId] = useState<number | null>(null);
  const showClosedCutMessage = isMonthlyCutClosed
    && selectedMonthlyCutId !== dismissedClosedCutId;

  /* Aviso permanente mientras el corte esté en recálculo, cerrado, o la empresa/holding
     seleccionado no tenga cortes creados (criterios de aceptación del ticket). Prioridad:
     recálculo > cerrado > sin cortes — recálculo y cerrado exigen un corte ya seleccionado, así
     que en la práctica no compiten con "sin cortes". Va en el slot `banner` de PageLayout, a
     ancho completo fuera de la tarjeta principal. */
  const statusBannerNode = useMemo<React.ReactNode>(() => {
    if (showRecalculatingMessage) {
      return (
        <Banner
          variant="info"
          density="compact"
          icon="alert"
          iconColor={colors.feedback.alert300}
          background={colors.feedback.alert100}
          textVariant="small-regular"
          rounded={false}
          className="px-4 md:px-8 lg:px-16"
          text={[
            { text: translate('absence:approval.messages.recalculatingPart1'), emphasis: false },
            { text: translate('absence:approval.messages.recalculatingPart1Emphasis'), emphasis: true },
            { text: translate('absence:approval.messages.recalculatingPart2'), emphasis: false },
            { text: translate('absence:approval.messages.recalculatingPart2Emphasis'), emphasis: true },
            { text: translate('absence:approval.messages.recalculatingPart3'), emphasis: false },
          ]}
          onDismiss={() => setDismissedRecalculationCutId(selectedMonthlyCutId)}
          dismissLabel={translate('absence:approval.actions.dismissMessage')}
          data-testid="absence-approval-status-message"
        />
      );
    }

    if (showClosedCutMessage) {
      return (
        <Banner
          variant="info"
          density="compact"
          icon="info"
          iconColor={colors.important.intermediate}
          background={colors.notification.closedBackground}
          textVariant="small-regular"
          rounded={false}
          className="px-4 md:px-8 lg:px-16"
          text={[
            { text: translate('absence:approval.messages.closedCutEmphasis'), emphasis: true },
            { text: translate('absence:approval.messages.closedCut'), emphasis: false },
          ]}
          onDismiss={() => setDismissedClosedCutId(selectedMonthlyCutId)}
          dismissLabel={translate('absence:approval.actions.dismissMessage')}
          data-testid="absence-approval-status-message"
        />
      );
    }

    if (showNoCutsMessage && noCutsMessageParts) {
      return (
        <Banner
          variant="error"
          density="compact"
          icon="info"
          iconColor={colors.feedback.error300}
          textVariant="small-regular"
          rounded={false}
          className="px-4 md:px-8 lg:px-16"
          text={noCutsMessageParts}
          onDismiss={() => setDismissedNoCutsScope(selectedCompanyValue)}
          dismissLabel={translate('absence:approval.actions.dismissMessage')}
          data-testid="absence-approval-no-cuts-message"
        />
      );
    }

    return null;
  }, [
    showRecalculatingMessage,
    showClosedCutMessage,
    showNoCutsMessage,
    noCutsMessageParts,
    selectedMonthlyCutId,
    selectedCompanyValue,
    translate,
  ]);

  return (
    <PageLayout banner={statusBannerNode}>
      <div className="flex flex-col gap-6" data-testid="absence-approval-list-page">
        <div className="flex flex-col gap-4">
          <div className="flex justify-end">
            <Switch
              checked={isDecimalFormat}
              onChange={onHourFormatToggle}
              label={translate('absence:approval.decimalSwitch.label')}
              tooltipText={translate('absence:approval.decimalSwitch.tooltip')}
              tooltipTitle={translate('absence:approval.decimalSwitch.label')}
              tooltipPlacement="left"
              data-testid="absence-approval-decimal-switch"
            />
          </div>

          <div
            className="flex flex-row flex-wrap gap-4"
            data-testid="absence-approval-counters"
          >
            {counters.map((counter) => (
              <IndicatorCard
                key={counter.key}
                label={counter.label}
                value={counter.value}
                padValue={false}
                tooltipText={counter.tooltip}
                tooltipTitle={counter.tooltipTitle}
                variant={counter.variant}
                muted={counter.muted}
                isSelected={counter.isSelected}
                onClick={() => onCounterSelect(counter.key)}
                data-testid={`absence-approval-counter-${counter.key}`}
              />
            ))}
          </div>
        </div>

        <DynamicFiltersCard
          fields={filterFields}
          columns={4}
          footer={(
            <div className="flex flex-col gap-4">
              <button
                type="button"
                onClick={dimensionalFilter.toggleExpanded}
                disabled={isAdvancedFiltersDisabled}
                className="self-start flex items-center gap-1 font-medium text-sm cursor-pointer bg-transparent border-none p-0 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ color: colors.important.main }}
                aria-expanded={dimensionalFilter.isExpanded}
                data-testid="absence-approval-advanced-filters-toggle"
              >
                {dimensionalFilter.isExpanded
                  ? translate('attendance:dimensionalFilter.hide')
                  : translate('attendance:dimensionalFilter.showAdvancedFilters')}
                <IconScout name={dimensionalFilter.isExpanded ? 'angleUp' : 'angleDown'} size={16} />
              </button>

              {dimensionalFilter.isExpanded && (
                /* Sin botón Buscar: los filtros avanzados se aplican solos al cambiar. */
                <EmployeeDimensionalFilter
                  config={dimensionalConfig}
                  state={dimensionalFilter}
                  companyLocked={isCompanyLocked}
                />
              )}
            </div>
          )}
        />

        <PermissionGuard action="view">
          <div className="flex justify-end">
            {/* ⚠️ La descarga efectiva (Excel de 2 hojas) queda fuera de esta pasada — sin
                columnas de hoja confirmadas. Se respeta la regla de habilitación del botón.
                Sin selector de formato: Excel es la única opción por ahora, así que el botón
                actúa directo en vez de abrir un dropdown de un solo elemento. */}
            <LinkButton
              iconLeft="import"
              onClick={() => undefined}
              disabled={!canDownload}
              className="focus:outline-none focus:ring-0 [&:focus]:!shadow-none"
              data-testid="absence-approval-download-button"
            >
              {translate('common:actions.download')}
            </LinkButton>
          </div>
        </PermissionGuard>

        <Table
          columns={columns}
          data={rows}
          totalRows={total}
          isLoading={isLoading}
          serverSideSort
          onSortChange={onSortChange}
          onSearch={onSearch}
          pagination={{
            page,
            pageSize,
            total,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          data-testid="absence-approval-table"
        />
      </div>
    </PageLayout>
  );
};

export default AbsenceApproval;
